import React from 'react';
import { useState } from "react";
import { ArrowRight, Play, Star, Menu, X, Zap, Users, ChevronLeft, ChevronRight, Camera, Image, Package, ShoppingCart, Truck, Shield } from 'lucide-react';

// Helper Component for a single product image
// MUST be defined outside or within the main component to be available
const ProductImage = ({ src, alt, className }) => (
  <div className={`absolute rounded-lg overflow-hidden shadow-xl transition-transform duration-300 hover:scale-[1.03] hover:z-10 cursor-pointer ${className}`}>
    {/* Fallback styling for images that might not exist */}
    <img 
      src={src || 'placeholder-image.jpg'} 
      alt={alt} 
      className="w-full h-full object-cover" 
    />
  </div>
);


export default function QuickCommerce() {
  // NOTE: You currently have 8 entries for 'MANGALAM9.png'. 
  // The scattered layout requires 9 images, so I've added one more repeated image 
  // to ensure all slots are filled without breaking the component.
  const products = [
    '/Images/MANGALAM12.png', // 0
    '/Images/MANGALAM9.png', // 1
    '/Images/MANGALAM13.png', // 2
    '/Images/MANGALAM.png', // 3
    '/Images/MANGALAM14.png', // 4
  ];
const [isModalOpen, setIsModalOpen] = useState(false);
const [currentImageIndex, setCurrentImageIndex] = useState(0);

const handleNextImage = () => {
  setCurrentImageIndex((prev) => (prev + 1) % 4);
};

const handlePrevImage = () => {
  setCurrentImageIndex((prev) => (prev - 1 + 4) % 4);
};

  const platforms = [
    { name: 'Blinkit', logo: '🛒', color: 'bg-yellow-400' },
 
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm py-4 px-6 pt-10">
        <div className="flex items-center justify-center">
          {/* Centered Logo and Title */}
          <div className="flex items-center gap-[10px]">
            <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
              <Package className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-xl font-semibold text-gray-800">
              Tamil Nadu Cooperative
            </h1>
          </div>
        </div>
      </header>

<div className="w-full -mt-8">
  {/* Overview Section - Image Style UI */}
  <section className="py-10 px-6 bg-white -ml-20">
    <div className="max-w-6xl mx-auto">
      <h2 className="text-3xl font-bold text-gray-900 mb-6">
        Overview
      </h2>
      
      <div className="bg-blue-100 rounded-lg border-l-4 border-blue-600 shadow-sm p-8">
        <p className="text-gray-700 leading-relaxed text-lg">
          Quick Commerce is Tamil Nadu’s initiative to deliver essential cooperative products—groceries, vegetables, dairy, and household supplies—directly to citizens with speed, affordability, and reliability.
        </p>
      </div>
    </div>
  </section>
</div>

{/* Feature Cards Section - With Background Image */}
<section className="py-15 px-6 relative overflow-hidden min-h-[400px] flex items-center">

  {/* Background Image - NO opacity layer */}
  <div
    className="absolute inset-0 bg-cover bg-center bg-no-repeat"
    style={{
      backgroundImage: `url('/Images/Gemini_Generated_Image_lgpqdwlgpqdwlgpq.png')`,
      zIndex: 0,
    }}
  ></div>

  <div className="w-full relative z-10 pr-[10px]">
    <div className="grid md:grid-cols-2 gap-4 max-w-2xl ml-auto">

      {/* Feature Card */}
      <div className="bg-white rounded-xl shadow-lg p-5 flex items-start gap-3 hover:shadow-2xl hover:scale-105 transition-all duration-300 border border-gray-200">
        <div className="p-3 rounded-xl flex-shrink-0 border-2 border-blue-600 text-blue-600">
          <Truck className="w-5 h-5" />
        </div>
        <div>
          <h3 className="font-bold text-gray-800">
            Real-time delivery tracking
          </h3>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-5 flex items-start gap-3 hover:shadow-2xl hover:scale-105 transition-all duration-300 border border-gray-200">
        <div className="p-3 rounded-xl flex-shrink-0 border-2 border-blue-600 text-blue-600">
          <ShoppingCart className="w-5 h-5" />
        </div>
        <div>
          <h3 className="font-bold text-gray-800">
            Doorstep convenience
          </h3>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-5 flex items-start gap-3 hover:shadow-2xl hover:scale-105 transition-all duration-300 border border-gray-200">
        <div className="p-3 rounded-xl flex-shrink-0 border-2 border-blue-600 text-blue-600">
          <Package className="w-5 h-5" />
        </div>
        <div>
          <h3 className="font-bold text-gray-800">
            Fair prices through cooperatives
          </h3>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-5 flex items-start gap-3 hover:shadow-2xl hover:scale-105 transition-all duration-300 border border-gray-200">
        <div className="p-3 rounded-xl flex-shrink-0 border-2 border-blue-600 text-blue-600">
          <Shield className="w-5 h-5" />
        </div>
        <div>
          <h3 className="font-bold text-gray-800">
            Safe & transparent supply chain
          </h3>
        </div>
      </div>

    </div>
  </div>
</section>



{/* 🚀 PRODUCT SHOWCASE SECTION 🚀 */}
<section className="py-12 px-6 bg-white overflow-hidden relative">
  {/* Background Decorative Icons */}
  <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-20">
    <Camera className="absolute top-10 left-10 w-16 h-16 text-blue-500 rotate-12" />
    <Image className="absolute top-32 right-20 w-20 h-20 text-blue-500 -rotate-12" />
    <Camera className="absolute bottom-20 left-1/4 w-12 h-12 text-blue-500 rotate-45" />
    <Image className="absolute top-1/2 right-10 w-16 h-16 text-blue-500 -rotate-6" />
  </div>

  <div className="max-w-7xl mx-auto">
    <div className="flex flex-col lg:flex-row items-center justify-between gap-8">

      {/* Left - 3D Carousel with Center Focus */}
      <div className="w-full lg:w-[70%] flex justify-center lg:justify-start">
        <div className="relative w-full max-w-4xl h-80 md:h-96" style={{ marginLeft: '10px' }}>
          
          {/* Container for all 3 images */}
          <div className="absolute inset-0 flex items-center justify-center">
            
{/* Left Shadow Image - MORE VISIBLE */}
<div
  className="absolute left-0 md:left-10 w-48 h-48 md:w-56 md:h-56 z-10 transition-all duration-700 ease-out opacity-90 blur-[1px]"
  style={{ transform: 'scale(0.8) translateX(16px)' }}
>
  <div className="w-full h-full rounded-2xl overflow-hidden shadow-xl bg-white border-2 border-gray-300 relative">
    <img
      src={products[(currentImageIndex - 1 + products.length) % products.length]}
      alt="Previous product"
      className="w-full h-full object-cover"
    />
    {/* lighter overlay */}
    <div className="absolute inset-0 bg-black/20"></div>
  </div>
</div>



            {/* Center Big Image - CLEAR & HIGHLIGHTED */}
            <div
              className="absolute left-1/2 -translate-x-1/2 w-64 h-64 md:w-80 md:h-80 z-30 transition-all duration-700 ease-out"
            >
<div className="w-full h-full rounded-3xl overflow-hidden shadow-2xl bg-white">
                <img
                  src={products[currentImageIndex]}
                  alt="Current product"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

        {/* Right Shadow Image - MORE VISIBLE */}
<div
  className="absolute right-0 md:right-10 w-48 h-48 md:w-56 md:h-56 z-10 transition-all duration-700 ease-out opacity-90 blur-[1px]"
  style={{ transform: 'scale(0.8) translateX(-16px)' }}
>
  <div className="w-full h-full rounded-2xl overflow-hidden shadow-xl bg-white border-2 border-gray-300 relative">
    <img
      src={products[(currentImageIndex + 1) % products.length]}
      alt="Next product"
      className="w-full h-full object-cover"
    />
    {/* lighter overlay */}
    <div className="absolute inset-0 bg-black/20"></div>
  </div>
</div>

          </div>
          
          {/* Navigation Arrows */}
          <button
            onClick={handlePrevImage}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-40 bg-blue-600 hover:bg-blue-700 text-white rounded-full p-3 md:p-4 shadow-2xl transition-all duration-300 hover:scale-110"
          >
            <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
          </button>
          <button
            onClick={handleNextImage}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-40 bg-blue-600 hover:bg-blue-700 text-white rounded-full p-3 md:p-4 shadow-2xl transition-all duration-300 hover:scale-110"
          >
            <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
          </button>
          
          {/* Indicator Dots */}
          <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 z-40 flex gap-2">
            {products.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === currentImageIndex
                    ? 'bg-blue-600 w-8'
                    : 'bg-gray-400 w-2 hover:bg-blue-400'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Right - View All Button */}
      <div className="w-full lg:w-[30%] flex justify-center lg:justify-start mt-8 lg:mt-0">
        <button
          onClick={() => setIsModalOpen(true)}
          className="w-full max-w-xs bg-blue-600 text-white py-5 px-8 rounded-xl text-lg font-semibold tracking-wide uppercase shadow-xl hover:bg-blue-700 hover:shadow-2xl hover:scale-105 transition-all duration-300 flex items-center justify-center gap-3"
        >
          View All Products
          <span className="text-2xl">→</span>
        </button>
      </div>

    </div>
  </div>
</section>

  {/* Quick Commerce Platforms Section - 80% Width */}
<section className="py-12 px-6 bg-gray-50">
  <div className="w-[80%] mx-auto text-center">
    <h2 className="text-2xl font-bold text-gray-800 mb-8">
      Locate on quick commerce website
    </h2>
    <p className="text-sm text-gray-600 mb-6">
      Available on all major platforms
    </p>

    <div className="flex justify-center items-center gap-8 flex-wrap">
      {platforms.map((platform, index) => (
        <div
          key={index}
          className="flex flex-col items-center gap-2 cursor-pointer hover:transform hover:scale-110 transition-transform"
        >
          <div className={`w-16 h-16 ${platform.color} rounded-full flex items-center justify-center shadow-lg text-2xl`}>
            {platform.logo}
          </div>
          <span className="text-sm font-medium text-gray-700">
            {platform.name}
          </span>
        </div>
      ))}
    </div>
  </div>
</section>

{isModalOpen && (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
    <div className="bg-white w-full max-w-5xl rounded-lg overflow-hidden shadow-xl max-h-[90vh] flex flex-col">
      
      {/* Header */}
      <div className="bg-blue-600 px-6 py-4 flex items-center justify-between">
        <h3 className="text-white text-2xl font-bold">All Products</h3>
        <button
          onClick={() => setIsModalOpen(false)}
          className="text-white font-bold text-2xl hover:text-gray-200"
        >
          ×
        </button>
      </div>
      
      {/* Content */}
      <div className="p-6 overflow-y-auto flex-1">
        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {products.map((product, idx) => (
            <div
              key={idx}
              className="border rounded-lg overflow-hidden flex flex-col items-center bg-gray-50 hover:shadow-lg transition"
            >
              <img
                src={product}
                alt={`Product ${idx + 1}`}
                className="w-full h-40 object-cover"
              />
              <div className="p-3 text-center">
                <h4 className="font-semibold text-gray-800">Product {idx + 1}</h4>
                <p className="text-gray-600 text-sm mt-1">
                  Description for product {idx + 1}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
)}


    </div>
  );
}